// La barra entre las pastillas y la tabla de Ventas, como `.table-toolbar` del mock: conteo, Ticket
// prom · Volumen · % SK de lo listado, Meta día de las tiendas, «vs 7d» de la cadena y el paginador.
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatMXN } from '@/shared/lib/format/money';
import { loadedResultLabel, SALES_NOUN, type LoadedScope } from '@/shared/patterns/LoadedScope';
import { TableToolbar, ToolbarMetric } from '@/shared/patterns/TableToolbar';
import type { BackofficeSale } from '@/api/services/retail-wizard/contract';
import type { SalesKpisResponse } from '@/api/services/retail-wizard/backoffice/homeContract';
import type { NetworkGoal } from '@/domains/retail/admin/home/lib/homeMetrics';
import {
    dayGoalPercent,
    goalTone,
    signedPercent,
    unitsVsBaselinePercent,
} from '../lib/ventasMetrics';
import { computeStats } from './VentasStatsBar';

export interface VentasPager {
    /** Página vigente, desde 1. */
    page: number;
    pageCount: number;
    onPrevious: () => void;
    onNext: () => void;
}

export interface VentasToolbarProps {
    /** Lo que lista la tabla, sin cortar en páginas. `null` = todavía no hay lista. */
    sales: BackofficeSale[] | null;
    scope: LoadedScope | null;
    /** KPIs del día de la cadena; `undefined` mientras cargan o si fallaron. */
    dayKpis?: SalesKpisResponse;
    /** Meta del día de las tiendas con meta; `undefined` mientras carga o si no se pudo leer. */
    dayGoal?: NetworkGoal;
    /** Las metas todavía se están leyendo; distingue «cargando» de «no se pudo leer». */
    dayGoalLoading?: boolean;
    pager?: VentasPager;
    /** Qué decir en lugar del conteo cuando no hay lista, como «Lectura fallida». */
    statusLabel?: string;
}

const GOAL_NO_SOURCE = 'No hay metas cargadas para hoy en esta cadena.';
const GOAL_SOLD_UNKNOWN = 'No se pudo leer lo vendido hoy por las tiendas con meta.';
const GOAL_UNREADABLE = 'No se pudieron leer las metas de las tiendas.';
const GOAL_LOADING = 'Cargando las metas de las tiendas…';
const BASELINE_NO_SOURCE = 'No hubo ventas el mismo día de la semana pasada para comparar.';
const KPIS_UNREADABLE = 'No se pudieron leer los indicadores del día.';

/** «$141.5K» para que la barra quepa en un renglón; la cifra exacta va en el tooltip. */
function formatMoneyCompact(cents: number): string {
    const pesos = cents / 100;
    if (Math.abs(pesos) >= 1_000_000) return `$${(pesos / 1_000_000).toFixed(1)}M`;
    if (Math.abs(pesos) >= 1_000) return `$${(pesos / 1_000).toFixed(1)}K`;
    return formatMXN(pesos);
}

function withExact(cents: number, note: string | undefined): string {
    const exact = formatMXN(cents / 100);
    return note === undefined ? exact : `${exact} · ${note}`;
}

const PAGER_BUTTON =
    'flex size-5 items-center justify-center rounded border border-border bg-card text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40';

function GoalMetric({ goal, loading }: { goal: NetworkGoal | undefined; loading: boolean }) {
    const { t } = useTranslation('ventas');
    const label = t('stats.metaDia', { defaultValue: 'Meta día' });
    if (!goal) {
        return (
            <ToolbarMetric
                label={label}
                value="—"
                title={loading ? GOAL_LOADING : GOAL_UNREADABLE}
            />
        );
    }
    const pct = goal.soldCents === null ? null : dayGoalPercent(goal.soldCents, goal.goalCents);
    return (
        <ToolbarMetric
            label={label}
            value={pct === null ? '—' : `${String(pct)}%`}
            tone={pct === null ? 'neutral' : goalTone(pct)}
            title={
                pct === null || goal.goalCents === null || goal.soldCents === null
                    ? goal.goalCents === null
                        ? GOAL_NO_SOURCE
                        : GOAL_SOLD_UNKNOWN
                    : `Vendido hoy ${formatMXN(goal.soldCents / 100)} de ${formatMXN(goal.goalCents / 100)} de meta, en las ${String(goal.storesWithGoal)} tiendas con meta`
            }
        />
    );
}

function DayMetrics({ kpis }: { kpis: SalesKpisResponse | undefined }) {
    const { t } = useTranslation('ventas');
    const vsLabel = t('stats.vs7d', { defaultValue: 'vs 7d' });
    if (!kpis) {
        return <ToolbarMetric label={vsLabel} value="—" title={KPIS_UNREADABLE} />;
    }

    const baseline = kpis.baselineSameDayLastWeekUnits;
    const vsPct = unitsVsBaselinePercent(kpis.unitsToday, baseline);
    return (
        <ToolbarMetric
            label={vsLabel}
            value={vsPct === null ? '—' : signedPercent(vsPct)}
            tone={vsPct === null ? 'neutral' : vsPct < 0 ? 'danger' : 'success'}
            title={
                vsPct === null
                    ? BASELINE_NO_SOURCE
                    : `${String(kpis.unitsToday)} piezas hoy contra ${String(baseline)} el mismo día de la semana pasada, en toda la cadena`
            }
        />
    );
}

export function VentasToolbar({
    sales,
    scope,
    dayKpis,
    dayGoal,
    dayGoalLoading = false,
    pager,
    statusLabel,
}: VentasToolbarProps) {
    const { t } = useTranslation('ventas');
    const stats = sales === null ? null : computeStats(sales);
    // Las métricas describen solo lo cargado: el title lo dice, y más fuerte si faltan páginas.
    const metricsNote =
        stats === null
            ? undefined
            : scope?.hasMore
              ? t('stats.alcanceParcial', {
                    n: stats.total,
                    defaultValue:
                        'Calculado sobre las {{n}} ventas cargadas — faltan páginas por traer',
                })
              : t('stats.alcance', {
                    n: stats.total,
                    defaultValue: 'Calculado sobre las {{n}} ventas cargadas',
                });

    return (
        <TableToolbar className="flex-nowrap gap-[3px] px-2 [&_span]:whitespace-nowrap">
            {sales !== null && scope !== null ? (
                <span className="whitespace-nowrap text-[10.5px]">
                    {loadedResultLabel(sales.length, scope, SALES_NOUN)}
                </span>
            ) : statusLabel !== undefined ? (
                <span className="whitespace-nowrap text-[10.5px]">{statusLabel}</span>
            ) : null}
            {stats === null ? null : (
                <>
                    <ToolbarMetric
                        label={t('stats.ticketPromedio', { defaultValue: 'Ticket prom' })}
                        value={formatMoneyCompact(stats.ticketPromedioCents)}
                        title={withExact(stats.ticketPromedioCents, metricsNote)}
                    />
                    <ToolbarMetric
                        label={t('stats.volumen', { defaultValue: 'Volumen' })}
                        value={formatMoneyCompact(stats.volumenCents)}
                        title={withExact(stats.volumenCents, metricsNote)}
                    />
                    <ToolbarMetric
                        label={t('stats.pctSk', { defaultValue: '% SK' })}
                        value={`${String(stats.pctSureKeep)}%`}
                        tone="success"
                        title={metricsNote}
                    />
                    <GoalMetric goal={dayGoal} loading={dayGoalLoading} />
                    <DayMetrics kpis={dayKpis} />
                </>
            )}
            <div className="flex-1" />
            {pager ? (
                <div className="flex flex-none items-center gap-0.5 text-[10.5px] text-foreground">
                    <button
                        type="button"
                        aria-label={t('pagina.anterior', { defaultValue: 'Página anterior' })}
                        disabled={pager.page <= 1}
                        onClick={pager.onPrevious}
                        className={PAGER_BUTTON}
                    >
                        <ChevronLeft className="size-3" aria-hidden />
                    </button>
                    <span className="tabular-nums">
                        {pager.page}/{pager.pageCount}
                    </span>
                    <button
                        type="button"
                        aria-label={t('pagina.siguiente', { defaultValue: 'Página siguiente' })}
                        disabled={pager.page >= pager.pageCount}
                        onClick={pager.onNext}
                        className={PAGER_BUTTON}
                    >
                        <ChevronRight className="size-3" aria-hidden />
                    </button>
                </div>
            ) : null}
        </TableToolbar>
    );
}
